/**
 * API path constants for mod-czna-authorities endpoints.
 */
export var API_PATHS = {
    /**
     * Path: /czna-authorities/records
     * GET: Search and list authority records with filtering, sorting and pagination
     * Response: CollectionResponse<RecordSummary>
     */
    CZNA_AUTHORITIES_RECORDS: 'czna-authorities/records',
    /** CZNA-972 — role-scoped union of proposals and records awaiting review. */
    CZNA_AUTHORITIES_REVIEW_QUEUE: 'czna-authorities/review-queue',
    /** CZNA-1054 — proposals and records returned to the signed-in creator. */
    CZNA_AUTHORITIES_RETURNED_QUEUE: 'czna-authorities/review-queue/returned',
    /** CZNA-1421 — sigla (040 $a) facet of each queue. GET → ReviewQueueSiglasResponse. */
    CZNA_AUTHORITIES_REVIEW_QUEUE_SIGLAS: 'czna-authorities/review-queue/siglas',
    CZNA_AUTHORITIES_RETURNED_QUEUE_SIGLAS: 'czna-authorities/review-queue/returned/siglas',
    /** CZNA-1644 — kind facet of each queue. GET ?sigla&withoutSigla → ReviewQueueKindsResponse. */
    CZNA_AUTHORITIES_REVIEW_QUEUE_KINDS: 'czna-authorities/review-queue/kinds',
    CZNA_AUTHORITIES_RETURNED_QUEUE_KINDS: 'czna-authorities/review-queue/returned/kinds',
    /**
     * CZNA-1596 — public error reports for the NK supervisor of the record's type.
     * GET ?status&recordId&offset&limit → CollectionResponse<ErrorReportListItem>;
     * `/{id}` → ErrorReportDetail; POST `/{id}/status` → ErrorReportDetail;
     * `/summary` → ErrorReportInboxSummary (every user).
     */
    CZNA_AUTHORITIES_ERROR_REPORTS: 'czna-authorities/error-reports',
    CZNA_AUTHORITIES_PROPOSALS: 'czna-authorities/proposals',
    /**
     * CZNA-595 — proposal counts per (status, proposalType) for the workflow tab badges.
     * GET: one aggregate for all seven tabs, narrowed by the same ownership params as the
     * list (`createdBy`, `organizationId`).
     * Response: ProposalStatusCount[] — groups with no rows are absent, not zero.
     */
    CZNA_AUTHORITIES_PROPOSALS_COUNTS: 'czna-authorities/proposals/counts',
    /** CZNA-553 — printable list of proposals awaiting approval in a period. */
    CZNA_AUTHORITIES_PROPOSALS_PENDING_REPORT: 'czna-authorities/proposals/pending-report',
    /** CZNA-598 — which authority types the signed-in user may start a proposal for. */
    CZNA_AUTHORITIES_PROPOSALS_ALLOWED_ACTIONS: 'czna-authorities/proposals/allowed-actions',
    CZNA_AUTHORITIES_RECORDS_EXPORT: 'czna-authorities/records/export',
    /** CZNA-217 — fields and subfields the query line can address. GET → FieldQueryCatalog. */
    CZNA_AUTHORITIES_RECORDS_FIELD_QUERY_FIELDS: 'czna-authorities/records/field-query/fields',
    /**
     * CZNA-217 — most frequent stored values of a field/subfield starting with a prefix
     * (at most 30). GET ?field&subfield&prefix&limit → FieldValuesResponse.
     */
    CZNA_AUTHORITIES_RECORDS_FIELD_VALUES: 'czna-authorities/records/field-values',
    /**
     * DB-061 — the source systems the corpus actually holds: every distinct
     * `source_system` with at least one ACTIVE record, sorted ascending.
     * GET → SourceSystemsResponse.
     *
     * <p>The "Zdrojový systém" sidebar filter reads its offering from here instead of
     * enumerating it, the same de-enumeration path CZNA-312/CZNA-457 took for the type
     * filter. The hardcoded list had drifted badly: it offered three values the corpus
     * has no record for and omitted ALEPH, which 99.8 % of records carry.
     */
    CZNA_AUTHORITIES_RECORDS_SOURCE_SYSTEMS: 'czna-authorities/records/source-systems',
    /**
     * CZNA-216 — alphabetical heading "rejstřík" (register) browse.
     * GET: heading-level entries (PREFERRED + VARIANT) ordered by Czech collation,
     * positioned by an `anchor` string with keyset forward/backward windows.
     * Response: RecordIndexResponse.
     */
    CZNA_AUTHORITIES_RECORDS_INDEX: 'czna-authorities/records/index',
    /**
     * CZNA-1168 — languages of the register's VARIANT headings with counts, most frequent
     * first; the offering of the "Jazyk" filter. `?recordType=` scopes it like the browse.
     * GET → IndexLanguagesResponse.
     */
    CZNA_AUTHORITIES_RECORDS_INDEX_LANGUAGES: 'czna-authorities/records/index/languages',
    /**
     * CZNA-305 E3 — read-only MARC catalog browser.
     * GET → { fields, _meta } (108 tags: labels, indicators.allowed, subfields
     * with repeatable_default/mandatory, input_policy, subfields_closed).
     */
    CZNA_MODULE_SETTINGS_MARC_CATALOG: 'czna-authorities/module-settings/marc-catalog',
    /**
     * CZNA-478 — form config checked against the corpus. Append `/{TYPE}`.
     * Computed live per call (measured 1.0-2.6 s on the 1.29M-record corpus), so
     * the caller must render a loading state rather than assume it is instant.
     */
    CZNA_MODULE_SETTINGS_ANALYSIS: 'czna-authorities/module-settings/analysis',
    /**
     * CZNA-456 — named form-config templates. A type has N templates, keyed
     * `form_config.{TYPE}.{templateId}`; `default` is the reserved id of the
     * packaged one.
     * GET (optional `?type=`) → FormConfigTemplateList.
     * POST → CreateFormConfigResponse (201); body carries either `copyFrom`
     * (an existing settingKey to duplicate) or `value`, never both.
     */
    CZNA_MODULE_SETTINGS_FORM_CONFIGS: 'czna-authorities/module-settings/form-configs',
    /**
     * CZNA-499 — customized form configs left behind by a changed packaged
     * default. GET → FormConfigDriftList (an empty `items` is the healthy state,
     * still a 200).
     */
    CZNA_MODULE_SETTINGS_DEFAULT_DRIFT: 'czna-authorities/module-settings/default-drift',
    CZNA_AUTHORITIES_IMPORT_BATCHES: 'czna-authorities/import/batches',
    /** CZNA-1343 — import stages in execution order + derivation rules. GET → ImportPipelineDescription. */
    CZNA_AUTHORITIES_IMPORT_PIPELINE: 'czna-authorities/import/pipeline',
    CZNA_AUTHORITIES_VOCABULARIES: 'czna-authorities/vocabularies',
    /**
     * CZNA-312 (WP-B/WP-C) — vocabulary-type registry. GET → VocabularyTypesResponse.
     * Replaces the FE de-enumeration of CLOSED vocab types: the admin GUI reads
     * the type catalog (code, labels, validationType, bindings) from this endpoint.
     * Optional ?validationType=OPEN|CLOSED (400 otherwise), ?includeBindings=true.
     */
    CZNA_AUTHORITIES_VOCABULARY_TYPES: 'czna-authorities/vocabulary-types',
    /**
     * Path: /czna-authorities/url-metadata
     * GET: Fetch title metadata (title, og:title, description) from an external URL.
     * Used by the "fetch title from URL" action button on the proposal form (TASK-044).
     */
    CZNA_AUTHORITIES_URL_METADATA: 'czna-authorities/url-metadata',
    /**
     * CZNA-367 — external identifier lookup (ISNI/Wikidata/ORCID) for MARC 024.
     * GET ?source=isni|wikidata|orcid&familyName=&givenName= → ExternalIdentifierSearchResult.
     * At least one of familyName/givenName is required; the other is optional.
     */
    CZNA_AUTHORITIES_EXTERNAL_IDENTIFIERS: 'czna-authorities/external-identifiers',
    /**
     * TASK-159 / AV-01 — ad-hoc validation endpoint.
     * POST: validate a MARC record without persisting; returns ValidationResult.
     */
    CZNA_VALIDATION_VALIDATE_RECORD: 'czna-authorities/validation/validate-record',
    /**
     * TASK-159 / AV-01 — kick off async bulk revalidate job (scope: ALL | TYPE | RECORD).
     */
    CZNA_VALIDATION_REVALIDATE: 'czna-authorities/validation/revalidate',
    /**
     * TASK-159 / AV-01 — currently active (QUEUED or RUNNING) job, or 204 if idle.
     * GET. Used by the progress banner to recover state after page reload.
     */
    CZNA_VALIDATION_REVALIDATE_CURRENT: 'czna-authorities/validation/revalidate/current',
    /**
     * CZNA-330 / AV-03 — corpus-quality dashboard read API.
     * GET /quality/latest (latest run, 204 if none), /quality/runs (history),
     * /quality/runs/{id} (detail), /quality/trend (trend points).
     */
    CZNA_VALIDATION_QUALITY_LATEST: 'czna-authorities/validation/quality/latest',
    CZNA_VALIDATION_QUALITY_RUNS: 'czna-authorities/validation/quality/runs',
    CZNA_VALIDATION_QUALITY_TREND: 'czna-authorities/validation/quality/trend',
    /** CZNA-1620 — did the validation rules change after the last full revalidation. GET. */
    CZNA_VALIDATION_QUALITY_RULESET_STATUS: 'czna-authorities/validation/quality/ruleset-status',
    /**
     * CZNA-1217 — corpus checks of the "Kvalita korpusu" tab. GET /latest (last stored run,
     * 204 if none); findings via {@link CZNA_CORPUS_CHECK_FINDINGS}. Running them is POST
     * /validation/revalidate with scope CORPUS_CHECKS.
     */
    CZNA_CORPUS_CHECKS_LATEST: 'czna-authorities/validation/corpus-checks/latest',
    /**
     * CZNA-245 / CZNA-244 — validation-rule administration (mvl-rule-admin).
     * GET (list with filters), POST (create MANUAL rule).
     * Response (GET): { totalRecords, rules: ValidationRule[] }.
     */
    CZNA_VALIDATION_RULES: 'czna-authorities/validation/rules',
    /**
     * CZNA-245 / CZNA-244 — dropdown vocabularies (RecordType, ValidationEvent,
     * Severity, Source) for filters and the detail form. GET → ValidationRuleMeta.
     */
    CZNA_VALIDATION_RULES_META: 'czna-authorities/validation/rules/meta',
    /**
     * CZNA-245 — MVL expression editor live diagnostics + autocomplete.
     * POST → MvlAssistResponse. Reused from the existing mvl/assist endpoint.
     */
    CZNA_VALIDATION_MVL_ASSIST: 'czna-authorities/validation/mvl/assist',
    /**
     * CZNA-229 (AV-01) — read-only MVL rule tester. POST one MVL expression against
     * an inline MARC record (→ MvlTestResult) or a small corpus sample
     * (→ MvlCorpusTestResult). Stateless; nothing is persisted.
     */
    CZNA_VALIDATION_MVL_TEST: 'czna-authorities/validation/mvl/test',
    /**
     * CZNA-365 (epic CZNA-297) — vocabulary derivation rule admin GUI.
     * GET (list all rules, plain array, ordered by vocabularyType),
     * POST (create rule, 201).
     */
    CZNA_VOCABULARY_DERIVATION_RULES: 'czna-authorities/admin/vocabulary/derivation-rules',
    /**
     * CZNA-365 — derive-now action. POST ?ruleId=&dryRun= → DeriveReport.
     * Same endpoint for the dry-run preview and the real run.
     */
    CZNA_VOCABULARY_DERIVATION_RULES_DERIVE: 'czna-authorities/admin/vocabulary/derivation-rules/derive',
    /**
     * CZNA-365 — run audit trail. GET ?ruleId=&offset=&limit= → plain array,
     * newest first. No `X-Total-Records` header (unlike the validation-rule
     * history endpoint) — treat as a bounded page, not a fully-counted list.
     */
    CZNA_VOCABULARY_DERIVATION_RULES_RUNS: 'czna-authorities/admin/vocabulary/derivation-rules/runs',
    /**
     * The semantic catalog (local ontology, ADR-0007). GET (no view) → the
     * full ontology; `?view=binds` → the flattened bind index; `?view=trace`
     * → per-tag label trace, ontology vs. each authority type's form template
     * (CZNA-461); `?view=componentBindings` (CZNA-381 /
     * CZNA-433) → { bindings: ComponentBinding[] }, every bind with a subfield
     * (CZNA-438 dropped the enum-backed restriction) — the source for the
     * derivation-rule componentType picker (repointed from the retired Semantic
     * Component Registry's `?view=bindings`).
     */
    CZNA_SEMANTIC_CATALOG: 'czna-authorities/semantic-catalog',
    /**
     * CZNA-1562 — OAI-PMH harvest observability (`OaiHarvestController`), under `/oai`
     * beside the public endpoint. GET ?since → HarvestStatistics;
     * GET ?from&until&clientIp&verb&status&errorCode&limit&cursor → HarvestLogPage.
     */
    OAI_HARVEST_STATISTICS: 'oai/harvest-statistics',
    OAI_HARVEST_LOG: 'oai/harvest-log',
};
/**
 * CZNA-245 / CZNA-244 — single validation-rule detail / update / delete.
 * GET (200/404), PUT (200/400/404/409 optimistic-lock), DELETE (204/409).
 */
export var CZNA_VALIDATION_RULE_DETAIL = function (id) {
    return "czna-authorities/validation/rules/".concat(id);
};
/**
 * CZNA-245 / CZNA-244 — audit trail for a single rule.
 * GET → { totalRecords, history: ValidationRuleHistoryEntry[] }.
 */
export var CZNA_VALIDATION_RULE_HISTORY = function (id) {
    return "czna-authorities/validation/rules/".concat(id, "/history");
};
/** CZNA-1217 — violations of one corpus check in a stored run (paged). */
export var CZNA_CORPUS_CHECK_FINDINGS = function (jobId) {
    return "czna-authorities/validation/corpus-checks/runs/".concat(jobId, "/findings");
};
/**
 * TASK-159 / AV-01 — poll a revalidate job by id.
 */
export var CZNA_VALIDATION_REVALIDATE_JOB = function (jobId) {
    return "czna-authorities/validation/revalidate/".concat(jobId);
};
/**
 * Returns proposal detail endpoint path for a specific proposal ID.
 */
export var CZNA_AUTHORITIES_PROPOSAL_DETAIL = function (id) {
    return "czna-authorities/proposals/".concat(id);
};
/**
 * Returns proposal submit endpoint path for a specific proposal ID.
 */
export var CZNA_AUTHORITIES_PROPOSAL_SUBMIT = function (id) {
    return "czna-authorities/proposals/".concat(id, "/submit");
};
/**
 * CZNA-581 — returns the "publish directly" ("Publikovat") path for a proposal.
 */
export var CZNA_AUTHORITIES_PROPOSAL_PUBLISH = function (id) {
    return "czna-authorities/proposals/".concat(id, "/publish");
};
/**
 * CZNA-552 — returns the "forward to national review" path for a proposal.
 */
export var CZNA_AUTHORITIES_PROPOSAL_FORWARD = function (id) {
    return "czna-authorities/proposals/".concat(id, "/forward");
};
/**
 * Returns proposal approve endpoint path for a specific proposal ID.
 */
export var CZNA_AUTHORITIES_PROPOSAL_APPROVE = function (id) {
    return "czna-authorities/proposals/".concat(id, "/approve");
};
/**
 * Returns proposal reject endpoint path for a specific proposal ID.
 */
export var CZNA_AUTHORITIES_PROPOSAL_REJECT = function (id) {
    return "czna-authorities/proposals/".concat(id, "/reject");
};
/**
 * CZNA-1531 — reopens a change proposal over the current version of its record.
 */
export var CZNA_AUTHORITIES_PROPOSAL_REBASE = function (id) {
    return "czna-authorities/proposals/".concat(id, "/rebase");
};
/**
 * Returns proposal request-changes endpoint path for a specific proposal ID.
 */
export var CZNA_AUTHORITIES_PROPOSAL_REQUEST_CHANGES = function (id) {
    return "czna-authorities/proposals/".concat(id, "/request-changes");
};
/** CZNA-949 — the author's written answer to a return. */
export var CZNA_AUTHORITIES_PROPOSAL_MESSAGES = function (id) {
    return "czna-authorities/proposals/".concat(id, "/messages");
};
/**
 * Returns import batch items endpoint path for a specific batch ID.
 */
export var CZNA_AUTHORITIES_IMPORT_BATCH_ITEMS = function (batchId) {
    return "czna-authorities/import/batches/".concat(batchId, "/items");
};
/**
 * CZNA-1344 — the stage protocol of one import batch.
 */
export var CZNA_AUTHORITIES_IMPORT_BATCH_PROTOCOL = function (batchId) {
    return "czna-authorities/import/batches/".concat(batchId, "/protocol");
};
/**
 * Returns import batch item detail endpoint path for a specific batch and item.
 */
export var CZNA_AUTHORITIES_IMPORT_BATCH_ITEM_DETAIL = function (batchId, itemId) {
    return "czna-authorities/import/batches/".concat(batchId, "/items/").concat(itemId);
};
/** CZNA-1470 — one vocabulary type in the registry (PUT renames it). */
export var CZNA_AUTHORITIES_VOCABULARY_TYPE_DETAIL = function (id) {
    return "czna-authorities/vocabulary-types/".concat(id);
};
/**
 * Returns vocabulary detail endpoint path for a specific vocabulary ID.
 */
export var CZNA_AUTHORITIES_VOCABULARY_DETAIL = function (id) {
    return "czna-authorities/vocabularies/".concat(id);
};
/**
 * CZNA-804 — usage report of one vocabulary against the corpus (202 while computing).
 */
export var CZNA_AUTHORITIES_VOCABULARY_USAGE = function (typeCode) {
    return "czna-authorities/vocabularies/usage/".concat(encodeURIComponent(typeCode));
};
/**
 * CZNA-1346 — near-duplicate check before a term of `typeCode` is created
 * (`?value=` is the proposed heading).
 */
export var CZNA_AUTHORITIES_VOCABULARY_SIMILAR = function (typeCode) {
    return "czna-authorities/vocabularies/similar/".concat(encodeURIComponent(typeCode));
};
/**
 * TASK-084 — record detail path (for soft-delete + edit).
 */
export var CZNA_AUTHORITIES_RECORD_DETAIL = function (id) {
    return "czna-authorities/records/".concat(id);
};
/**
 * CZNA-1513 — GET the records behind one register row (the row's heading id);
 * `?language=` and `?status=` as on the register. → RecordIndexGroupResponse.
 */
export var CZNA_AUTHORITIES_RECORDS_INDEX_GROUP = function (headingId) {
    return "czna-authorities/records/index/".concat(headingId, "/records");
};
/**
 * TASK-084 — PATCH /records/{id}/review (approve | redirect).
 */
export var CZNA_AUTHORITIES_RECORD_REVIEW = function (id) {
    return "czna-authorities/records/".concat(id, "/review");
};
/**
 * TASK-084 — GET /records/{id}/references-count (redirect preview counts).
 */
export var CZNA_AUTHORITIES_RECORD_REFERENCES_COUNT = function (id) {
    return "czna-authorities/records/".concat(id, "/references-count");
};
/**
 * CZNA-586 — GET /records/{id}/history?offset=…&limit=… (versions with field-level changes).
 */
export var CZNA_AUTHORITIES_RECORD_HISTORY = function (id, offset, limit) {
    return "czna-authorities/records/".concat(id, "/history?offset=").concat(offset, "&limit=").concat(limit);
};
/**
 * CZNA-193 (TASK-150) — GET /records/{id}/skos?format=… (read-only SKOS view).
 * `format` is encoded defensively even though callers pass a closed union.
 */
export var CZNA_AUTHORITIES_RECORD_SKOS = function (id, format) {
    return "czna-authorities/records/".concat(id, "/skos?format=").concat(encodeURIComponent(format));
};
/**
 * CZNA-1617 — GET /records/{id}/export: the round-trip MARCXML of ONE record, byte for byte
 * what the list's `records/export?format=MARCXML` emits for it (the record detail's
 * "Exportovat MARCXML").
 */
export var CZNA_AUTHORITIES_RECORD_EXPORT = function (id) {
    return "czna-authorities/records/".concat(encodeURIComponent(id), "/export");
};
/**
 * TASK-109 (AR-08) — POST /records/{id}/merge (merge source into target).
 */
export var CZNA_AUTHORITIES_RECORD_MERGE = function (id) {
    return "czna-authorities/records/".concat(id, "/merge");
};
/**
 * TASK-109 (AR-08) — GET /records/{id}/merge/preview?targetId=… (read-only preview).
 */
export var CZNA_AUTHORITIES_RECORD_MERGE_PREVIEW = function (id, targetId) {
    return "czna-authorities/records/".concat(id, "/merge/preview?targetId=").concat(targetId);
};
/**
 * CZNA-365 — single vocabulary derivation rule detail / update / delete.
 * GET (200/404), PUT (200/400/404/409 optimistic-lock), DELETE (204/404).
 */
export var CZNA_VOCABULARY_DERIVATION_RULE_DETAIL = function (id) {
    return "czna-authorities/admin/vocabulary/derivation-rules/".concat(id);
};
/**
 * CZNA-456 — compiled EFC for ONE named template.
 *
 * The type-only `effective/{type}` path (built inline by `useFormConfig`) is
 * unchanged and still means "this type's DEFAULT template" (plan R3), so a
 * caller that does not care about templates keeps working untouched. This
 * two-segment variant is a pure addition.
 */
export var CZNA_MODULE_SETTINGS_EFFECTIVE_TEMPLATE = function (type, templateId) { return "czna-authorities/module-settings/effective/".concat(type, "/").concat(templateId); };
/**
 * CZNA-456 — a single `module_setting` row. DELETE is dual-semantic and
 * dispatches server-side on `user_created`: 200 + body = the packaged row was
 * RESET to its default; 204 = a template NK created was physically deleted.
 */
export var CZNA_MODULE_SETTING_DETAIL = function (key) {
    return "czna-authorities/module-settings/".concat(key);
};
/**
 * CZNA-499 — three-way comparison for one key: merge base, stored value, new
 * packaged default, plus the per-tab/per-field classification. 409 when the key
 * has no packaged default (a template NK created), which is why the compare
 * action is only offered on packaged rows.
 */
export var CZNA_MODULE_SETTING_DEFAULT_DRIFT = function (key) {
    return "czna-authorities/module-settings/".concat(key, "/default-drift");
};
/** CZNA-499 — POST the admin's decision (take / keep / merge). */
export var CZNA_MODULE_SETTING_DEFAULT_DRIFT_RESOLVE = function (key) {
    return "czna-authorities/module-settings/".concat(key, "/default-drift/resolve");
};
